import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { MessageOutlined, SearchOutlined, UsergroupAddOutlined, ArrowsAltOutlined } from "@ant-design/icons";
import { Button, Empty, Grid, Input, List, Popover, Spin, Typography } from "antd";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useChatConversations, useChatUnreadCount } from "../../../../hooks/useChat";
import { useMyGroups } from "../../../../hooks/useChatGroup";
import { useOnlineUsers } from "../../../../hooks/usePresence";
import { ChatConversationDto } from "../../../../models/chat/ChatConversationDto";
import { ChatGroupListDto } from "../../../../models/chat/ChatGroup";
import { EmployeeListDto } from "../../../../models/dtos/EmployeeList.dto";
import { RootState } from "../../../../stores/Store";
import { getStickerEmoji } from "../../../../utils/chatStickers";
import { CommonBadge } from "../badge/CommonBadge";
import { CommonAvatar } from "../avatar/CommonAvatar";
import { CreateGroupModal } from "./CreateGroupModal";
import { openChatConversation, openChatGroup } from "./ChatEvents";
import { getProfileName, useChatUserProfiles } from "./useChatUserProfiles";
import { employeeService } from "../../../hrm/services/EmployeeService";

const MESSAGES_ROUTE = "/messages";

interface MessagesButtonHeaderProps {
  tone?: "light" | "dark";
}

type UnifiedChatItem =
  | { key: string; kind: "direct"; sortMs: number; conversation: ChatConversationDto }
  | { key: string; kind: "group"; sortMs: number; group: ChatGroupListDto };

const getDirectPreview = (conversation: ChatConversationDto) => {
  if (conversation.lastMessageType === "STICKER") return `${getStickerEmoji(conversation.lastMessageContent) ?? ""} Nhãn dán`.trim();
  return conversation.lastMessageContent || "None";
};

const getGroupPreview = (group: ChatGroupListDto) => {
  const senderPrefix = group.lastMessageSenderName ? `${group.lastMessageSenderName}: ` : "";
  if (group.lastMessageContent == null) return "None";
  if (group.lastMessageType === "SYSTEM") return group.lastMessageContent;
  if (group.lastMessageType === "STICKER") return `${senderPrefix}${getStickerEmoji(group.lastMessageContent) ?? ""} Nhãn dán`.trim();
  return `${senderPrefix}${group.lastMessageContent}`;
};

export const MessagesButtonHeader = memo(({ tone = "light" }: MessagesButtonHeaderProps) => {
  const screens = Grid.useBreakpoint();
  const popoverWidth = screens.sm ? 424 : 256;
  const navigate = useNavigate();
  const { userId } = useSelector((state: RootState) => state.auth);

  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingCreatedGroupIdForClose, setPendingCreatedGroupIdForClose] = useState<string | null>(null);
  const [pendingCreatedGroupStartedAt, setPendingCreatedGroupStartedAt] = useState<number | null>(null);

  const enabled = !!userId;
  const { data: directUnreadCount = 0 } = useChatUnreadCount(enabled);
  const {
    data: conversations = [],
    isLoading: conversationsLoading,
    isFetching: conversationsFetching,
    refetch: refetchConversations,
  } = useChatConversations(enabled && open);
  const { data: groups = [], isLoading: groupsLoading, isFetching: groupsFetching, refetch: refetchGroups } = useMyGroups(enabled);
  const onlineUsers = useOnlineUsers(enabled && open);

  const trimmedKeyword = keyword.trim();
  const { data: searchResult, isFetching: isSearching } = useQuery({
    queryKey: ["chat", "employee-search", trimmedKeyword],
    queryFn: () => employeeService.search(trimmedKeyword, 0, 10),
    enabled: enabled && open && trimmedKeyword.length >= 2,
  });

  const directProfileUserIds = useMemo(() => (open ? conversations.map((conversation) => conversation.partnerUserId) : []), [conversations, open]);
  const profileByUserId = useChatUserProfiles(directProfileUserIds);

  const groupUnreadCount = useMemo(() => groups.reduce((total, group) => total + (group.unreadCount ?? 0), 0), [groups]);
  const unreadCount = directUnreadCount + groupUnreadCount;

  const unifiedChats = useMemo<UnifiedChatItem[]>(() => {
    const items: UnifiedChatItem[] = [];

    conversations.forEach((conversation) =>
      items.push({
        key: `d:${conversation.partnerUserId}`,
        kind: "direct",
        sortMs: conversation.lastMessageEpochMs ?? 0,
        conversation,
      }),
    );

    groups.forEach((group) =>
      items.push({
        key: `g:${group.id}`,
        kind: "group",
        sortMs: group.lastMessageEpochMs ?? 0,
        group,
      }),
    );

    return items.sort((a, b) => b.sortMs - a.sortMs);
  }, [conversations, groups]);

  const employees = useMemo(
    () => (searchResult?.content ?? []).filter((employee) => employee.userId && employee.userId !== userId),
    [searchResult?.content, userId],
  );

  const showSearch = trimmedKeyword.length >= 2;
  const waitingForCreatedGroupData = !!pendingCreatedGroupIdForClose;
  const listLoading = conversationsLoading || groupsLoading || conversationsFetching || groupsFetching;
  const showListBlockingLoading = listLoading && unifiedChats.length === 0;

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);

      if (!nextOpen) {
        setKeyword("");
        return;
      }

      refetchGroups();
      refetchConversations();
    },
    [refetchConversations, refetchGroups],
  );

  const closePanel = useCallback(() => {
    setOpen(false);
    setKeyword("");
  }, []);

  const openFloatingDirect = useCallback(
    (partnerUserId?: string, partnerName?: string) => {
      if (!partnerUserId) return;
      openChatConversation({ partnerUserId, partnerName });
      closePanel();
    },
    [closePanel],
  );

  const openFloatingGroup = useCallback(
    (groupId?: string, groupName?: string, avatarUrl?: string | null) => {
      if (!groupId) return;
      openChatGroup({ groupId, groupName, avatarUrl });
      closePanel();
    },
    [closePanel],
  );

  const goToMessages = useCallback(() => {
    closePanel();
    navigate(MESSAGES_ROUTE);
  }, [closePanel, navigate]);

  const pendingCreatedGroupFromList = useMemo(
    () =>
      unifiedChats.find(
        (item): item is Extract<UnifiedChatItem, { kind: "group" }> => item.kind === "group" && item.group.id === pendingCreatedGroupIdForClose,
      ),
    [pendingCreatedGroupIdForClose, unifiedChats],
  );

  useEffect(() => {
    if (!createOpen || !pendingCreatedGroupIdForClose || !pendingCreatedGroupFromList) return;

    const elapsedMs = pendingCreatedGroupStartedAt ? Date.now() - pendingCreatedGroupStartedAt : 0;
    const remainingMs = Math.max(280 - elapsedMs, 0);

    let frame = 0;
    const timeout = window.setTimeout(() => {
      frame = window.requestAnimationFrame(() => {
        setCreateOpen(false);
        setPendingCreatedGroupIdForClose(null);
        setPendingCreatedGroupStartedAt(null);
        openFloatingGroup(pendingCreatedGroupFromList.group.id, pendingCreatedGroupFromList.group.name, pendingCreatedGroupFromList.group.avatarUrl);
      });
    }, remainingMs);

    return () => {
      window.clearTimeout(timeout);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [createOpen, openFloatingGroup, pendingCreatedGroupFromList, pendingCreatedGroupIdForClose, pendingCreatedGroupStartedAt]);

  const renderDirectConversation = useCallback(
    (conversation: ChatConversationDto) => {
      const profile = profileByUserId.get(conversation.partnerUserId);
      const name = getProfileName(profile, conversation.partnerName || conversation.partnerUserId);

      return (
        <List.Item className="!p-0">
          <button
            type="button"
            onClick={() => openFloatingDirect(conversation.partnerUserId, conversation.partnerName)}
            onContextMenu={(event) => {
              event.preventDefault();
              openFloatingDirect(conversation.partnerUserId, conversation.partnerName);
            }}
            className="flex w-full cursor-pointer items-center gap-3 border-none bg-white px-4 py-3 text-left transition-colors hover:bg-[#f7fafc] rounded-none outline-none"
          >
            <div className="relative shrink-0">
              <CommonBadge count={conversation.unreadCount} size="small" overflowCount={9}>
                <CommonAvatar size={38} src={profile?.avatarId} name={name} className="bg-[#0068ff] text-white" />
              </CommonBadge>
              {onlineUsers.has(conversation.partnerUserId) && (
                <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white bg-[#22c55e]" />
              )}
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <Typography.Text ellipsis className="min-w-0 text-[12px] font-semibold leading-5 text-[#111827]">
                  {name}
                </Typography.Text>
                <span className="shrink-0 text-[10px] leading-4 text-[#8a8f98]">{conversation.lastMessageTime}</span>
              </div>
              <div
                className={[
                  "mt-0.5 truncate text-[11px] leading-4",
                  conversation.unreadCount > 0 ? "font-medium text-[#111827]" : "text-[#7b8190]",
                ].join(" ")}
              >
                {conversation.lastMessageFromMe ? "Bạn: " : ""}
                {getDirectPreview(conversation)}
              </div>
            </div>
          </button>
        </List.Item>
      );
    },
    [onlineUsers, openFloatingDirect, profileByUserId],
  );

  const renderGroupConversation = useCallback(
    (group: ChatGroupListDto) => (
      <List.Item className="!p-0">
        <button
          type="button"
          onClick={() => openFloatingGroup(group.id, group.name, group.avatarUrl)}
          onContextMenu={(event) => {
            event.preventDefault();
            openFloatingGroup(group.id, group.name, group.avatarUrl);
          }}
          className="flex w-full cursor-pointer items-center gap-3 border-none bg-white px-4 py-3 text-left transition-colors hover:bg-[#f7fafc] rounded-none outline-none"
        >
          <div className="relative shrink-0">
            <CommonBadge count={group.unreadCount} size="small" overflowCount={9}>
              <CommonAvatar size={38} src={group.avatarUrl ?? undefined} name={group.name} className="bg-[#0a2240] text-white" />
            </CommonBadge>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <Typography.Text ellipsis className="min-w-0 text-[12px] font-semibold leading-5 text-[#111827]">
                {group.name} <span className="font-normal text-[#8a8f98]">({group.memberCount})</span>
              </Typography.Text>
              <span className="shrink-0 text-[10px] leading-4 text-[#8a8f98]">{group.lastMessageTime}</span>
            </div>
            <div
              className={["mt-0.5 truncate text-[11px] leading-4", group.unreadCount > 0 ? "font-medium text-[#111827]" : "text-[#7b8190]"].join(" ")}
            >
              {getGroupPreview(group)}
            </div>
          </div>
        </button>
      </List.Item>
    ),
    [openFloatingGroup],
  );

  const renderEmployee = useCallback(
    (employee: EmployeeListDto) => (
      <List.Item className="!p-0">
        <button
          type="button"
          onClick={() => openFloatingDirect(employee.userId, employee.fullName)}
          onContextMenu={(event) => {
            event.preventDefault();
            openFloatingDirect(employee.userId, employee.fullName);
          }}
          className="flex w-full cursor-pointer items-center gap-3 border-none bg-white px-4 py-3 text-left transition-colors hover:bg-[#f7fafc] rounded-none outline-none"
        >
          <div className="relative shrink-0">
            <CommonAvatar size={38} src={employee.avatarId} name={employee.fullName} className="bg-[#0068ff] text-white" />
            {onlineUsers.has(employee.userId) && (
              <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white bg-[#22c55e]" />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <Typography.Text ellipsis className="block text-[12px] font-semibold leading-5 text-[#111827]">
              {employee.fullName}
            </Typography.Text>
            <div className="truncate text-[11px] leading-4 text-[#7b8190]">
              {[employee.employeeCode, employee.jobTitle].filter(Boolean).join(" - ")}
            </div>
          </div>
        </button>
      </List.Item>
    ),
    [onlineUsers, openFloatingDirect],
  );

  const content = (
    <div className="relative w-full overflow-hidden rounded-none bg-white text-[12px] shadow-[0_18px_48px_rgba(15,23,42,0.18)]">
      {waitingForCreatedGroupData && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-white/85 backdrop-blur-[1px]">
          <Spin size="large" />
          <div className="text-sm font-medium text-[#475467]">Đang đồng bộ nhóm mới...</div>
        </div>
      )}
      <div className="border-b border-[#eef1f5] px-4 py-4">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="text-[18px] font-semibold leading-6 text-[#0b1220]">Messages</div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Button size="small" type="text" icon={<ArrowsAltOutlined />} className="!text-[#0068ff] !rounded-none" onClick={goToMessages} />
            <Button size="small" type="text" icon={<UsergroupAddOutlined />} className="!text-[#0068ff] !rounded-none" onClick={() => setCreateOpen(true)}>
              Tạo nhóm
            </Button>
          </div>
        </div>

        <div className="relative mt-3">
          <SearchOutlined className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-[12px] text-[#8a8f98]" />
          <Input
            allowClear
            size="small"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="Tìm nhân viên để chat..."
            className="!h-8 !rounded-none !pl-8"
          />
        </div>
      </div>

      <div className="max-h-[calc(100dvh-220px)] overflow-y-auto bg-white">
        {showSearch ? (
          isSearching ? (
            <div className="flex justify-center py-12">
              <Spin />
            </div>
          ) : employees.length === 0 ? (
            <div className="py-12">
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không tìm thấy nhân viên" />
            </div>
          ) : (
            <List split={false} dataSource={employees} renderItem={renderEmployee} />
          )
        ) : showListBlockingLoading ? (
          <div className="flex justify-center py-12">
            <Spin />
          </div>
        ) : unifiedChats.length === 0 ? (
          <div className="py-12">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có hội thoại" />
          </div>
        ) : (
          <List
            split={false}
            dataSource={unifiedChats}
            renderItem={(item) => (item.kind === "direct" ? renderDirectConversation(item.conversation) : renderGroupConversation(item.group))}
          />
        )}
      </div>
    </div>
  );

  if (!userId) return null;

  return (
    <>
      <Popover
        content={content}
        trigger="click"
        open={open}
        onOpenChange={handleOpenChange}
        placement="bottomRight"
        overlayStyle={{
          width: popoverWidth,
          maxWidth: popoverWidth,
        }}
        overlayInnerStyle={{
          padding: 0,
          borderRadius: 0,
          overflow: "hidden",
        }}
      >
        <button
          type="button"
          aria-label="Chat"
          onContextMenu={(event) => {
            event.preventDefault();
            handleOpenChange(true);
          }}
          className={[
            "box-border inline-flex h-[26px] w-[26px] min-w-[26px] max-w-[26px] items-center justify-center p-0 shrink-0 border-none rounded-none leading-none cursor-pointer outline-none transition-colors",
            tone === "light"
              ? "bg-white/10 text-white hover:bg-white/20"
              : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200",
          ].join(" ")}
        >
          <CommonBadge count={unreadCount} size="small" overflowCount={99}>
            <MessageOutlined
              style={{
                fontSize: 17,
                color: tone === "light" ? "#fff" : undefined,
              }}
            />
          </CommonBadge>
        </button>
      </Popover>

      <CreateGroupModal
        open={createOpen}
        waiting={waitingForCreatedGroupData}
        waitingText="Đang đồng bộ nhóm chat mới..."
        onClose={() => {
          if (waitingForCreatedGroupData) return;
          setCreateOpen(false);
        }}
        onCreated={(groupId) => {
          handleOpenChange(true);
          setKeyword("");
          setPendingCreatedGroupIdForClose(groupId);
          setPendingCreatedGroupStartedAt(Date.now());
        }}
      />
    </>
  );
});
